import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, BarChart, Bar } from "recharts";
import { Sparkles, TrendingUp } from "lucide-react";

function Card({ title, subtitle, children, testId }) {
  return (
    <div className="rounded-3xl bg-white shadow-card p-6" data-testid={testId}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">{subtitle}</div>
      <div className="font-heading text-xl text-ink-900 mt-1">{title}</div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

export default function Insights() {
  const t = useT();
  const insights = useQuery({
    queryKey: ["insights"],
    queryFn: async () => (await api.get("/insights")).data,
    retry: false,
  });
  const weekly = useQuery({
    queryKey: ["insights-weekly"],
    queryFn: async () => (await api.get("/insights/weekly")).data,
    retry: false,
  });

  const summary = insights.data?.summary || insights.data?.text || "";
  const themes = insights.data?.themes || [];
  const trendRaw =
    insights.data?.trend ||
    insights.data?.mood_trend ||
    weekly.data?.trend ||
    weekly.data?.days ||
    [];
  const trend = Array.isArray(trendRaw)
    ? trendRaw.map((p, i) => ({
        day: p.day || p.date || `D${i + 1}`,
        value: Number(p.value ?? p.energy ?? p.score ?? p.mood ?? 0),
      }))
    : [];

  const weeklyMetrics =
    weekly.data?.metrics ||
    (weekly.data && typeof weekly.data === "object" ? Object.entries(weekly.data).slice(0, 6).map(([k, v]) => ({ label: k, value: typeof v === "number" ? v : 0 })) : []);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.insights")}
        title={t("client.weeklyInsight")}
        subtitle={t("brand.tagline")}
      />

      {(insights.isLoading || weekly.isLoading) && <LoadingCard height="h-40" />}
      {insights.isError && !insights.isLoading && (
        <ErrorState message={errMsg(insights.error)} onRetry={() => insights.refetch()} />
      )}

      {!insights.isLoading && !insights.isError && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card testId="insights-summary" subtitle={t("nav.insights")} title="This week">
            <div className="flex gap-3 items-start">
              <Sparkles className="h-5 w-5 text-sage-600 shrink-0 mt-0.5" strokeWidth={1.75} />
              <p className="text-ink-700 whitespace-pre-wrap">{summary || t("common.empty")}</p>
            </div>
            {Array.isArray(themes) && themes.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {themes.map((th, i) => (
                  <span key={i} className="rounded-full bg-sage-100 text-sage-700 text-xs px-3 py-1 font-medium">
                    {typeof th === "string" ? th : (th.label || th.name)}
                  </span>
                ))}
              </div>
            )}
          </Card>

          <Card testId="insights-trend" subtitle={t("nav.rhythm")} title="Rhythm">
            {trend.length === 0 ? (
              <EmptyState icon={TrendingUp} title={t("common.empty")} className="py-8" />
            ) : (
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend} margin={{ left: 0, right: 0, top: 8, bottom: 0 }}>
                    <CartesianGrid stroke="#E8E4DD" strokeDasharray="3 3" />
                    <XAxis dataKey="day" fontSize={11} stroke="#8A847C" tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} stroke="#8A847C" tickLine={false} axisLine={false} width={24} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E4DD" }} />
                    <Line type="monotone" dataKey="value" stroke="#728468" strokeWidth={2} dot={{ r: 3, fill: "#728468" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card testId="insights-weekly" subtitle={t("client.weeklyInsight")} title="Weekly">
            {(!weeklyMetrics || weeklyMetrics.length === 0) ? (
              <EmptyState icon={TrendingUp} title={t("common.empty")} className="py-8" />
            ) : (
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyMetrics} margin={{ left: 0, right: 0, top: 8, bottom: 0 }}>
                    <CartesianGrid stroke="#E8E4DD" strokeDasharray="3 3" />
                    <XAxis dataKey="label" fontSize={10} stroke="#8A847C" tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} stroke="#8A847C" tickLine={false} axisLine={false} width={24} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E4DD" }} />
                    <Bar dataKey="value" fill="#D27D60" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
